/**
 * Investor History Panel
 *
 * Displays detailed prepayment pool transaction history for a selected investor
 */

import { Badge } from '../ui/badge';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/formatters';

interface InvestorHistory {
  current_balance: number;
  total_contributed: number;
  total_consumed: number;
  currency: string;
  transactions: Array<{
    id: string;
    transaction_type: string;
    amount: number;
    currency: string;
    created_at: string;
    description?: string;
    balance_after: number;
  }>;
}

interface InvestorHistoryContentProps {
  investorHistory: InvestorHistory;
}

const getTransactionIcon = (type: string) => {
  return type === 'CONTRIBUTION' ? (
    <ArrowUpRight className="h-4 w-4 text-green-600" />
  ) : (
    <ArrowDownRight className="h-4 w-4 text-blue-600" />
  );
};

const getTransactionBadge = (type: string) => {
  return type === 'CONTRIBUTION' ? (
    <Badge variant="success">Contribution</Badge>
  ) : (
    <Badge variant="secondary">Withdrawal</Badge>
  );
};

/**
 * Content-only version for use in dialogs
 */
export function InvestorHistoryContent({
  investorHistory,
}: InvestorHistoryContentProps) {
  return (
    <>
      <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="font-medium text-gray-600 dark:text-gray-400">
              Current Balance
            </div>
            <div className="text-lg font-bold">
              {formatCurrency(investorHistory.current_balance, {
                currency: investorHistory.currency,
              })}
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-600 dark:text-gray-400">
              Total Contributed
            </div>
            <div className="text-lg font-bold">
              {formatCurrency(investorHistory.total_contributed, {
                currency: investorHistory.currency,
              })}
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-600 dark:text-gray-400">
              Total Consumed
            </div>
            <div className="text-lg font-bold">
              {formatCurrency(investorHistory.total_consumed, {
                currency: investorHistory.currency,
              })}
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-600 dark:text-gray-400">
              Transactions
            </div>
            <div className="text-lg font-bold">
              {investorHistory.transactions.length}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {investorHistory.transactions.map((transaction) => (
          <div
            key={transaction.id}
            className="flex items-center justify-between p-3 border rounded-lg"
          >
            <div className="flex items-center gap-3">
              {getTransactionIcon(transaction.transaction_type)}
              <div>
                <div className="flex items-center gap-2">
                  {getTransactionBadge(transaction.transaction_type)}
                  <span className="text-sm font-medium">
                    {formatCurrency(transaction.amount, {
                      currency: transaction.currency,
                    })}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDate(transaction.created_at)}
                  {transaction.description && ` • ${transaction.description}`}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Balance After
              </div>
              <div className="text-sm font-bold">
                {formatCurrency(transaction.balance_after, {
                  currency: transaction.currency,
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
